import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Trophy, Timer, Target } from 'lucide-react';

const BallBalance = ({ onGameEnd }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('waiting'); // waiting, playing, finished
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);
  const [ballPosition, setBallPosition] = useState(50); // percentage from left
  const [platformTilt, setPlatformTilt] = useState(0); // -30 to 30 degrees
  const gameLoopRef = useRef(null);

  useEffect(() => {
    if (gameState === 'playing') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // Ball physics
      let velocity = 0;
      let acceleration = 0;
      const gravity = 0.3;
      const friction = 0.95;

      const gameLoop = () => {
        // Clear canvas
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);

        // Calculate acceleration based on platform tilt
        acceleration = platformTilt * 0.05;
        velocity += acceleration;
        velocity *= friction;

        // Update ball position
        let newPos = ballPosition + velocity;
        
        // Check boundaries
        if (newPos < 5) {
          newPos = 5;
          velocity = 0;
        }
        if (newPos > 95) {
          newPos = 95;
          velocity = 0;
        }

        setBallPosition(newPos);

        // Draw platform
        ctx.save();
        ctx.translate(width / 2, height * 0.7);
        ctx.rotate((platformTilt * Math.PI) / 180);
        
        // Platform gradient
        const gradient = ctx.createLinearGradient(-width * 0.4, 0, width * 0.4, 0);
        gradient.addColorStop(0, '#8b5cf6');
        gradient.addColorStop(0.5, '#a78bfa');
        gradient.addColorStop(1, '#8b5cf6');
        
        ctx.fillStyle = gradient;
        ctx.fillRect(-width * 0.4, -10, width * 0.8, 20);
        ctx.strokeStyle = '#6d28d9';
        ctx.lineWidth = 3;
        ctx.strokeRect(-width * 0.4, -10, width * 0.8, 20);
        ctx.restore();

        // Draw ball
        const ballX = (newPos / 100) * width;
        const ballY = height * 0.7 - 30;
        
        // Ball shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.beginPath();
        ctx.ellipse(ballX, height * 0.7 + 5, 20, 5, 0, 0, Math.PI * 2);
        ctx.fill();

        // Ball gradient
        const ballGradient = ctx.createRadialGradient(ballX - 5, ballY - 5, 5, ballX, ballY, 20);
        ballGradient.addColorStop(0, '#fbbf24');
        ballGradient.addColorStop(1, '#f59e0b');
        
        ctx.fillStyle = ballGradient;
        ctx.beginPath();
        ctx.arc(ballX, ballY, 20, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#d97706';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw center target zone
        const centerX = width / 2;
        const zoneWidth = 80;
        
        ctx.fillStyle = 'rgba(34, 197, 94, 0.2)';
        ctx.fillRect(centerX - zoneWidth / 2, height * 0.7 - 50, zoneWidth, 60);
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(centerX - zoneWidth / 2, height * 0.7 - 50, zoneWidth, 60);
        ctx.setLineDash([]);

        // Check if ball is in center zone - award points
        if (Math.abs(newPos - 50) < 10) {
          setScore(prev => prev + 1);
        }

        gameLoopRef.current = requestAnimationFrame(gameLoop);
      };

      gameLoop();

      return () => {
        if (gameLoopRef.current) {
          cancelAnimationFrame(gameLoopRef.current);
        }
      };
    }
  }, [gameState, platformTilt, ballPosition]);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('finished');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  useEffect(() => {
    if (gameState === 'finished') {
      // Stop the game loop
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
      // Call onGameEnd after a short delay to show the final screen
      setTimeout(() => {
        onGameEnd(score);
      }, 2000);
    }
  }, [gameState, score, onGameEnd]);

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setTimeLeft(45);
    setBallPosition(50);
    setPlatformTilt(0);
  };

  const handleTiltLeft = () => {
    setPlatformTilt(prev => Math.max(prev - 5, -30));
  };

  const handleTiltRight = () => {
    setPlatformTilt(prev => Math.min(prev + 5, 30));
  };

  const handleMouseMove = (e) => {
    if (gameState !== 'playing') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const centerX = canvas.width / 2;
    const distance = x - centerX;
    const tilt = (distance / centerX) * 30;
    setPlatformTilt(Math.max(-30, Math.min(30, tilt)));
  };

  const handleTouchMove = (e) => {
    if (gameState !== 'playing') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const centerX = canvas.width / 2;
    const distance = x - centerX;
    const tilt = (distance / centerX) * 30;
    setPlatformTilt(Math.max(-30, Math.min(30, tilt)));
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-lg p-6 border border-purple-500/30">
        {/* Game Stats */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2 text-yellow-400">
            <Trophy className="w-5 h-5" />
            <span className="font-bold text-xl">Score: {score}</span>
          </div>
          <div className="flex items-center gap-2 text-blue-400">
            <Timer className="w-5 h-5" />
            <span className="font-bold text-xl">{timeLeft}s</span>
          </div>
        </div>

        {/* Game Canvas */}
        <div className="relative bg-black rounded-lg overflow-hidden mb-4">
          <canvas
            ref={canvasRef}
            width={600}
            height={400}
            className="w-full h-auto touch-none"
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
            style={{ touchAction: 'none' }}
          />
          
          {gameState === 'waiting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
              <Target className="w-16 h-16 text-yellow-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Ball Balance</h3>
              <p className="text-gray-300 text-center mb-4 max-w-md px-4">
                Keep the ball balanced in the center zone! Move your mouse/finger to tilt the platform.
                Stay in the green zone to score points!
              </p>
              <Button onClick={startGame} className="gaming-button">
                Start Game
              </Button>
            </div>
          )}

          {gameState === 'finished' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90">
              <Trophy className="w-20 h-20 text-yellow-400 mb-4" />
              <h3 className="text-3xl font-bold text-white mb-2">Game Over!</h3>
              <p className="text-2xl text-yellow-400 font-bold mb-4">Final Score: {score}</p>
              <p className="text-gray-300 mb-6">Submitting your score...</p>
            </div>
          )}
        </div>

        {/* Mobile Controls */}
        <div className="flex gap-4 lg:hidden">
          <Button
            onClick={handleTiltLeft}
            disabled={gameState !== 'playing'}
            className="gaming-button flex-1"
          >
            ← Tilt Left
          </Button>
          <Button
            onClick={handleTiltRight}
            disabled={gameState !== 'playing'}
            className="gaming-button flex-1"
          >
            Tilt Right →
          </Button>
        </div>

        {/* Instructions */}
        <div className="mt-4 text-sm text-gray-400 text-center">
          <p className="mb-1">🖱️ Desktop: Move mouse left/right to tilt platform</p>
          <p>📱 Mobile: Touch and drag to tilt, or use buttons</p>
        </div>
      </div>
    </div>
  );
};

export default BallBalance;
